import { createContext, useContext } from 'react';
import type { IndicatorResponse } from '@/lib/schema/types';
import type { ObservationMetadata, ObservationRecord } from './types';

/**
 * Context object + hook split out of `ObservationContext.tsx` so that file
 * only exports the `ObservationProvider` component. React Fast Refresh
 * refuses to preserve state across reloads when a module exports a mix of
 * components and non-components — this split keeps HMR snappy during dev.
 */

export interface ObservationContextValue {
  state: ObservationRecord;
  setMetadata: (patch: Partial<ObservationMetadata>) => void;
  setResponse: (
    itemId: string,
    indicatorId: string,
    response: IndicatorResponse | null,
  ) => void;
  setItemNa: (itemId: string, value: boolean) => void;
  setItemNote: (itemId: string, note: string) => void;
  completeItem: (itemId: string) => void;
  reopenItem: (itemId: string) => void;
  markSubmitted: () => void;
}

export const ObservationContext = createContext<ObservationContextValue | null>(
  null,
);

export function useObservation(): ObservationContextValue {
  const ctx = useContext(ObservationContext);
  if (!ctx)
    throw new Error('useObservation must be used inside <ObservationProvider>');
  return ctx;
}
